// src/web/client/src/components/activity/ActivityLog.jsx
//
// Activity — ONE log (UX brief, Phase B2): the emails this app sent, with their
// opens and clicks, and — on the Platform plan — everything the brain did
// (restock reminders, review requests, loyalty changes, opens), merged by time,
// grouped by day. Runs of the same line in a day collapse into one row with a
// count. Clicking one of our emails opens its full preview.

import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Mail, CheckCircle, MousePointerClick, TrendingUp } from 'lucide-react';
import { API_BASE_URL } from '@/constants';
import { authenticatedFetch, SubscriptionRequiredError } from '@/utils/authenticatedFetch';
import { getShopDomain } from '@/utils/shopContext';
import { getActivityTimeline } from '@/platform/api';
import { useSubscriptionStatus } from '@/components/subscription/SubscriptionGuard';
import MessageDetailsDialog from './MessageDetailsDialog';
import { RANGES, KINDS, buildDays, dayLabel, timeOf } from './activityLogModel';

const MESSAGES_CAP = 200;
const KIND_COLOR = { emails: 'bg-indigo-500', restock: 'bg-teal-500', reviews: 'bg-amber-500', loyalty: 'bg-pink-500', engagement: 'bg-indigo-300' };

function Stat({ icon: Icon, value, label, tint }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{label}</p>
            <p className={`text-2xl font-bold ${tint}`}>{value}</p>
          </div>
          <Icon className={`w-8 h-8 ${tint}`} />
        </div>
      </CardContent>
    </Card>
  );
}

export default function ActivityLog() {
  const shopDomain = getShopDomain();
  const subscription = useSubscriptionStatus();
  const hasBrain = subscription?.hasFeatureAccess?.('platform_access') === true;

  const [range, setRange] = useState('30d');
  const [kind, setKind] = useState('all');
  const [messages, setMessages] = useState([]);
  const [messagesTotal, setMessagesTotal] = useState(0);
  const [platformDays, setPlatformDays] = useState([]);
  const [stats, setStats] = useState({ total: 0, sent: 0, clicked: 0, clickRate: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    if (!shopDomain) { setLoading(false); return undefined; }
    const r = RANGES.find((x) => x.value === range) || RANGES[1];
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const params = new URLSearchParams({ shop_domain: shopDomain, sort: '-createdAt', limit: String(MESSAGES_CAP), skip: '0', exclude_internal: 'true', period: r.messages });
        const [msgRes, statsRes, platform] = await Promise.all([
          authenticatedFetch(`${API_BASE_URL}/messages?${params.toString()}`),
          authenticatedFetch(`${API_BASE_URL}/messages/stats/overview?shop_domain=${encodeURIComponent(shopDomain)}`),
          hasBrain ? getActivityTimeline({ range: r.platform }).catch(() => null) : Promise.resolve(null),
        ]);
        if (!alive) return;
        const list = msgRes.ok ? await msgRes.json().catch(() => []) : [];
        setMessages(Array.isArray(list) ? list : []);
        const total = parseInt(msgRes.headers?.get?.('X-Total-Count') || '0', 10);
        setMessagesTotal(Number.isFinite(total) ? total : 0);
        if (statsRes.ok) {
          const s = await statsRes.json().catch(() => ({}));
          setStats({ total: s.total_messages || 0, sent: s.sent_messages || 0, clicked: s.clicked_messages || 0, clickRate: parseFloat(s.click_rate) || 0 });
        }
        setPlatformDays(platform?.days || []);
      } catch (e) {
        if (!alive) return;
        if (!(e instanceof SubscriptionRequiredError)) setError(e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [shopDomain, range, hasBrain, reloadKey]);

  const days = useMemo(() => buildDays({ messages, platformDays, kind }), [messages, platformDays, kind]);
  const totalShown = days.reduce((s, d) => s + d.total, 0);
  const kinds = hasBrain ? KINDS : KINDS.filter((k) => k.value === 'all' || k.value === 'emails');

  if (!shopDomain) {
    return <Card><CardContent className="py-10 text-center text-gray-700">We couldn't identify your store. Please reopen the app from your Shopify admin.</CardContent></Card>;
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Stat icon={Mail} tint="text-indigo-600" value={stats.total.toLocaleString()} label="Total emails" />
        <Stat icon={CheckCircle} tint="text-green-600" value={stats.sent.toLocaleString()} label="Sent" />
        <Stat icon={MousePointerClick} tint="text-blue-600" value={stats.clicked.toLocaleString()} label="Clicked" />
        <Stat icon={TrendingUp} tint="text-purple-600" value={`${stats.clickRate.toFixed(1)}%`} label="Click Rate" />
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between px-5 py-4 border-b">
            <p className="text-sm font-medium text-gray-900" data-testid="activity-count">
              {loading ? 'Loading…' : `${totalShown.toLocaleString()} ${totalShown === 1 ? 'event' : 'events'} · ${(RANGES.find((x) => x.value === range) || RANGES[1]).label.toLowerCase()}`}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <select aria-label="Range" value={range} onChange={(e) => setRange(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
                {RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              <select aria-label="Kind" value={kind} onChange={(e) => setKind(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
                {kinds.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </div>
          </div>

          {error ? (
            <div className="py-10 text-center text-gray-700">
              <p>Couldn’t load the activity.</p>
              <button type="button" className="mt-2 text-sm font-medium text-indigo-600" onClick={() => setReloadKey((k) => k + 1)}>Try again</button>
            </div>
          ) : loading ? (
            <div className="py-10 text-center text-sm text-gray-500">Loading…</div>
          ) : days.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-500">Nothing yet — it appears here as your customers order and as emails go out.</div>
          ) : (
            <div className="divide-y">
              {days.map((d) => (
                <div key={d.date} className="px-5 py-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-semibold text-gray-900">{dayLabel(d.date)}</span>
                    <span className="text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">{d.total}</span>
                  </div>
                  <div className="space-y-1">
                    {d.items.map((e) => {
                      const clickable = Boolean(e.message);
                      const when = e.count > 1 ? `${timeOf(e.from)}–${timeOf(e.to)}` : timeOf(e.at);
                      return (
                        <div
                          key={e.id}
                          className={`flex items-center gap-3 rounded-md px-2 py-1.5 ${clickable ? 'cursor-pointer hover:bg-gray-50' : ''} ${e.severity === 'error' ? 'bg-red-50' : ''}`}
                          role={clickable ? 'button' : undefined}
                          tabIndex={clickable ? 0 : undefined}
                          onClick={clickable ? () => setSelected(e.message) : undefined}
                          onKeyDown={clickable ? (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setSelected(e.message); } } : undefined}
                        >
                          <span className={`w-2 h-2 rounded-full shrink-0 ${KIND_COLOR[e.kind] || 'bg-gray-400'}`} />
                          <span className="flex-1 text-sm text-gray-800 min-w-0 truncate">
                            {e.label}
                            {e.count > 1 && <span className="ml-2 text-xs font-semibold text-gray-500" data-testid="run-count">×{e.count}</span>}
                            {e.detail && <span className="ml-2 text-xs text-gray-500">{e.detail}</span>}
                          </span>
                          <span className="text-xs text-gray-500 shrink-0">{when}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
          {messagesTotal > MESSAGES_CAP && (
            <p className="px-5 py-3 text-xs text-gray-500 border-t">Showing the latest {MESSAGES_CAP} of {messagesTotal.toLocaleString()} emails in this range.</p>
          )}
        </CardContent>
      </Card>

      <MessageDetailsDialog message={selected} open={Boolean(selected)} onOpenChange={(open) => { if (!open) setSelected(null); }} />
    </div>
  );
}
